import { z } from 'zod'

export const ScanStatusSchema = z.enum(['pending', 'processing', 'validation', 'completed', 'failed'])

export const PumpStationSchema = z.object({
  id: z
    .string()
    .uuid()
    .describe(
      'The official ID of the water station, that is used in our system, has to be a UUID. Use the station definition name and the "point de pompage" as matching key.'
    ),
  name: z.string().describe('Name of the pumping station. Written as "Point de pompage"')
})

export const ScanDataPointSchema = z.object({
  date: z
    .string()
    .describe(
      'Date of the reading, must be in ISO format using the Z notation on the Paris timezone. Written as "Date de la lecture"'
    ),
  numberOfRotations: z
    .number()
    .describe(
      'Number of hand written X marks under the Column "Nb. de rotation". This is the number of times the truck was filled, avoid counting the table grid lines as X marks, only count the hand written X marks.'
    ),
  volume: z
    .number()
    .describe(
      'Total volume used in cubic meters, entered by the driver. Written in the column "Total prélevé". If not mentioned mark as incoherent but calculate it using the number of rotations and the tank volume.'
    ),
  hasIncoherence: z.boolean(),
  reasoning: z
    .string()
    .describe(
      'Reasoning. In case of incoherence or not, explain why using logicals. You must write it in French.'
    ),
  pumpStationID: z
    .string()
    .uuid()
    .describe(
      'The official ID of the water station, that is used in our system, has to be a UUID and must be in the list of Pump Stations. Use "point de pompage" as matching key.'
    )
})

export const ScanExtractionSchema = z.object({
  site: z.object({
    ID: z
      .string()
      .uuid()
      .describe(
        'The official ID of the site, that is used in our system, has to be a UUID. Use the internal ID from the site definition and Numero de Chantier as matching key. If there is no close match, select the closest using a simple heuristic.'
      ),
    internalID: z.string().describe('Internal ID of the site, written as "Numero de Chantier"'),
    name: z.string().describe('Name of the mathing site, take this from the site definition.'),
    reasoning: z
      .string()
      .describe(
        'Reasoning for the site identification. Explain how you selected this site based on the document and the site definitions. In case of an incoherece in your selection and your selection because there was no close match, explain your reasoning. You must write it in French.'
      ),
    hasIncoherence: z.boolean()
  }),
  metadata: z.object({
    weekNumber: z.number().min(1).max(53).describe('Week number of the year. Written as "Semaine N: [1-53]"'),
    siteNumber: z.string().describe('This is the internal ID used by the client. Written as "Numero de Chantier"'),
    driverName: z.string().describe('Name of the driver who fillled the sheet. Written as "Nom du chauffeur"'),
    tankVolume: z.number().describe('Volume of the tank in liters. Written as "Volume du de citerne"'),
    pumpStations: z.array(PumpStationSchema)
  }),
  readings: z
    .array(ScanDataPointSchema)
    .describe(
      'Array of readings on the sheet. It should be a multiple of 7, one per day of the week for each pumping station'
    )
})

export const ScanSchema = z.object({
  ID: z.number().int(),
  name: z.string(),
  createdAt: z.string(),
  processedAt: z.string().nullable(),
  orgID: z.string(),
  status: ScanStatusSchema,
  extractedData: ScanExtractionSchema.nullable(),
  verifiedData: ScanExtractionSchema.nullable()
})

export type EiffageScan = z.infer<typeof ScanSchema>
export type ScanDataPoint = z.infer<typeof ScanDataPointSchema>
export type ScanExtraction = z.infer<typeof ScanExtractionSchema>
